import {
  ControlledSubstanceClientProfileField,
  ControlledSubstanceComplianceFailureField,
  ControlledSubstanceFailure,
  ControlledSubstanceStatus,
} from "common/types/controlledSubstances";
import { toHumanizedList } from "common/utils/strings";

import { ControlledSubstanceClassification, ControlledSubstancePolicy } from "../types";

/**
 * Formats product-line names as a singular or plural controlled-substance clause, e.g.
 * "Gabapentin is a controlled substance" or
 * "Gabapentin and Proin are controlled substances".
 */
export const getControlledProductLinesText = (productLineNames: string[]): string =>
  `${toHumanizedList(productLineNames)} ${
    productLineNames.length === 1 ? "is a controlled substance" : "are controlled substances"
  }`;

/** Whether any of `itemIds` is controlled under `policy`, at the state it was resolved at. */
export const getControlledSubstanceClassification = (
  policy: ControlledSubstancePolicy,
  itemIds: number[],
): ControlledSubstanceClassification => ({
  is_controlled: policy.items.some(
    (itemPolicy) =>
      itemIds.includes(itemPolicy.item_id) &&
      itemPolicy.controlled_substance_status === ControlledSubstanceStatus.Controlled,
  ),
  basis_state: policy.basis_state,
});

/**
 * Client fields a policy can require that checkout's items step and onboarding's profile step
 * already require, so the controlled-substances inputs never ask for them.
 */
const CLIENT_FIELDS_ASKED_EARLIER: ReadonlySet<string> =
  new Set<ControlledSubstanceClientProfileField>([
    "first_name",
    "last_name",
    "primary_contact_number",
  ]);

/** The required fields the controlled-substances inputs ask for. */
export const getFieldsAskedForControlledSubstances = <Field extends string>(
  fields: Field[],
): Field[] => fields.filter((field) => !CLIENT_FIELDS_ASKED_EARLIER.has(field));

/** The failures the controlled-substances inputs can clear: any naming a field they ask for. */
export const getFailuresClearedByControlledSubstances = (
  failures: ControlledSubstanceFailure[],
): ControlledSubstanceFailure[] =>
  failures.filter((failure) => getFieldsAskedForControlledSubstances(failure.fields).length);

/** Each failure's detail under every field it names, for the inputs that edit them. */
export const getFailureDetailsByField = (
  failures: ControlledSubstanceFailure[],
): Partial<Record<ControlledSubstanceComplianceFailureField, string[]>> =>
  failures
    .flatMap((failure) => failure.fields.map((field) => [field, failure.detail] as const))
    .reduce<Partial<Record<ControlledSubstanceComplianceFailureField, string[]>>>(
      (detailsByField, [field, detail]) => ({
        ...detailsByField,
        [field]: [...(detailsByField[field] ?? []), detail],
      }),
      {},
    );
